import { useState } from 'react';
import PublicFooter from '../../components/PublicFooter';
import PublicHeader from '../../components/PublicHeader';
import { Link } from '../../lib/router';

export default function PublicContactPage() {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState('idle');

  async function submit(event) {
    event.preventDefault();
    const nextEmail = email.trim();

    if (!event.currentTarget.checkValidity() || !nextEmail) {
      setFormState('error');
      event.currentTarget.reportValidity();
      return;
    }

    setFormState('submitting');
    try {
      const leadsUrl =
        process.env.REACT_APP_LEADS_URL ||
        'https://nextvisit-api.unwishlist.workers.dev/api/leads';
      const response = await fetch(leadsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nextEmail, source: 'contact-page' }),
      });

      if (!response.ok) throw new Error('Contact request failed.');
      setFormState('success');
    } catch {
      setFormState('submit-error');
    }
  }

  const formMessage =
    formState === 'error'
      ? 'Enter a valid business email.'
      : formState === 'submit-error'
        ? 'We couldn’t send your request. Please try again or email us directly.'
        : formState === 'success'
          ? 'Thanks — our team will be in touch to arrange a chat or demo.'
          : null;

  return (
    <main className="public-contact-page">
      <PublicHeader currentPage="contact" />

      <div className="contact-page-back">
        <Link className="contact-back-link" to="/">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="m11.75 4.75-5.25 5.25 5.25 5.25M7 10h7" />
          </svg>
          Back to home
        </Link>
      </div>

      <section className="contact-account-help" aria-labelledby="account-help-title">
        <div>
          <strong id="account-help-title" className="contact-account-help-title">
            Forgot your password?
          </strong>
          <p>
            Email{' '}
            <a
              className="contact-account-email"
              href="mailto:loyalqr.sg@gmail.com?subject=LoyalQR.sg%20account%20access%20help"
            >
              loyalqr.sg@gmail.com
            </a>{' '}
            from the address linked to your LoyalQR account and we’ll help you regain
            access.
          </p>
        </div>
        <a href="mailto:loyalqr.sg@gmail.com?subject=LoyalQR.sg%20account%20access%20help">
          Contact us for account help
        </a>
      </section>

      <section className="contact-layout">
        <div className="contact-intro">
          <h1>How can we help?</h1>
          <p>
            Whether you’re exploring LoyalQR, setting up your business, or need help with
            an existing account, our team is here for you.
          </p>
          <div className="contact-direct">
            <strong>General enquiries</strong>
            <a href="mailto:loyalqr.sg@gmail.com?subject=LoyalQR.sg%20enquiry">
              loyalqr.sg@gmail.com
            </a>
          </div>
        </div>

        <form
          className="contact-form"
          onSubmit={submit}
          aria-busy={formState === 'submitting'}
        >
          <div>
            <h2>See if LoyalQR fits your business</h2>
            <p>
              <strong>LoyalQR Basic is free.</strong> Drop your business email and our
              team will be in touch for a chat or demo.
            </p>
          </div>
          <label htmlFor="contact-business-email">Business email</label>
          <input
            id="contact-business-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength="254"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (formState !== 'idle') setFormState('idle');
            }}
            onInvalid={() => setFormState('error')}
            aria-describedby={formMessage ? 'contact-form-message' : undefined}
            disabled={formState === 'success'}
            required
          />
          <button
            type="submit"
            disabled={formState === 'submitting' || formState === 'success'}
          >
            {formState === 'submitting'
              ? 'Sending…'
              : formState === 'success'
                ? 'Request sent'
                : 'Request a chat or demo'}
          </button>
          {formMessage && (
            <p
              id="contact-form-message"
              className={formState === 'success' ? 'success' : ''}
              role={formState === 'error' || formState === 'submit-error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              {formMessage}
            </p>
          )}
        </form>
      </section>
      <PublicFooter />
    </main>
  );
}
